/**
 * Device identification: persistent device_id (localStorage), device metadata, iPad detection.
 * No URL exposure; no regeneration per page load.
 */

import UAParser from 'ua-parser-js';
import type { DeviceMeta, DeviceType } from './types';
import {
  OBSERVABILITY_ENABLED,
  DEVICE_ID_STORAGE_KEY,
} from './constants';

const STORAGE = typeof localStorage !== 'undefined' ? localStorage : null;

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older Safari / iOS
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateDeviceIdStorage(): string {
  if (!OBSERVABILITY_ENABLED || !STORAGE) {
    return '';
  }
  let id = STORAGE.getItem(DEVICE_ID_STORAGE_KEY);
  if (!id || typeof id !== 'string') {
    id = generateUUID();
    try {
      STORAGE.setItem(DEVICE_ID_STORAGE_KEY, id);
    } catch {
      // storage full or disabled
    }
  }
  return id;
}

let cachedDeviceId: string | null = null;

export function getDeviceId(): string {
  if (cachedDeviceId !== null) {
    return cachedDeviceId;
  }
  cachedDeviceId = getOrCreateDeviceIdStorage();
  return cachedDeviceId;
}

/** Call once at bootstrap to ensure device_id exists (e.g. before Faro init). */
export function ensureDeviceId(): string {
  return getDeviceId();
}

function inferDeviceType(): DeviceType {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  // iPadOS 13+ may report as Mac; use heuristics
  const isIPad =
    /iPad/.test(ua) ||
    (navigator.platform === 'MacIntel' && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1);
  if (isIPad) return 'iPad';
  if (/iPhone|iPod/.test(ua)) return 'iPhone';
  return 'Desktop';
}

function getOrientation(): string {
  if (typeof screen === 'undefined') return 'unknown';
  const o = (screen as { orientation?: { type?: string } }).orientation;
  if (o?.type) return o.type;
  if (typeof window !== 'undefined' && typeof (window as { orientation?: number }).orientation === 'number') {
    const w = (window as { orientation: number }).orientation;
    if (w === 0) return 'portrait-primary';
    if (w === 180) return 'portrait-secondary';
    if (w === 90 || w === -90) return 'landscape';
  }
  return 'unknown';
}

let cachedDeviceMeta: DeviceMeta | null = null;

export function getDeviceMetadata(): DeviceMeta {
  if (cachedDeviceMeta) return cachedDeviceMeta;

  const deviceId = getDeviceId();
  const deviceType = inferDeviceType();

  let os_version = '';
  let browser_name = '';
  let browser_version = '';
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    try {
      const parser = new UAParser(navigator.userAgent);
      const os = parser.getOS();
      const browser = parser.getBrowser();
      os_version = os.version ?? '';
      browser_name = browser.name ?? '';
      browser_version = browser.version ?? '';
    } catch {
      // ignore
    }
  }

  const screen_width = typeof screen !== 'undefined' ? screen.width : 0;
  const screen_height = typeof screen !== 'undefined' ? screen.height : 0;
  const device_pixel_ratio = typeof window !== 'undefined' && typeof window.devicePixelRatio === 'number'
    ? window.devicePixelRatio
    : 1;
  const orientation = getOrientation();

  cachedDeviceMeta = {
    device_id: deviceId,
    device_type: deviceType,
    os_version,
    browser_name,
    browser_version,
    screen_width,
    screen_height,
    device_pixel_ratio,
    orientation,
  };
  return cachedDeviceMeta;
}
